import { useRef, useState } from "react";
import { Box, Button, HStack, Image, Text } from "@chakra-ui/react";
import { OriginsApi } from "../api/origins";
import logger from "../analytics/logger";

const ACCEPT = "image/png,image/jpeg,image/webp,image/svg+xml";

type Props = {
  label: string;
  value: string | null;
  onChange: (url: string | null) => void;
  helperText?: string;
  /** Preview aspect ratio, e.g. 3/2 for the mobile logo. Defaults to 16/9. */
  previewRatio?: number;
  isDisabled?: boolean;
};

// Image upload control: presign → PUT to S3 → store the returned public URL.
// Mirrors the email-template upload flow (raw fetch PUT with a Content-Type
// header that must match what the backend signed).
export default function LogoUpload({
  label,
  value,
  onChange,
  helperText,
  previewRatio = 16 / 9,
  isDisabled = false,
}: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const pick = () => inputRef.current?.click();

  const onFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // allow re-selecting the same file later
    if (!file) return;
    setError(null);
    setUploading(true);
    try {
      const { uploadUrl, publicUrl } = await OriginsApi.getLogoUploadUrl(file.type);
      const put = await fetch(uploadUrl, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });
      if (!put.ok) throw new Error(`upload failed: ${put.status}`);
      onChange(publicUrl);
    } catch (err) {
      logger.error("origin_logo_upload_failed", err);
      setError("Upload failed. Use PNG, JPEG, WebP, or SVG.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <Box>
      <Text fontSize="xs" color="gray.500" mb={1}>
        {label}
      </Text>
      {helperText && (
        <Text fontSize="xs" color="gray.500" mb={2}>
          {helperText}
        </Text>
      )}
      {value && (
        <Box
          mb={2}
          maxW="240px"
          sx={{ aspectRatio: String(previewRatio) }}
          borderWidth="1px"
          borderRadius="md"
          overflow="hidden"
          bg="gray.50"
        >
          <Image src={value} alt={label} w="full" h="full" objectFit="contain" />
        </Box>
      )}
      <HStack>
        <Button size="sm" onClick={pick} isLoading={uploading} isDisabled={isDisabled}>
          {value ? "Replace image" : "Upload image"}
        </Button>
        {value && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => onChange(null)}
            isDisabled={isDisabled || uploading}
          >
            Remove
          </Button>
        )}
      </HStack>
      {error && (
        <Text fontSize="xs" color="red.500" mt={1}>
          {error}
        </Text>
      )}
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        hidden
        onChange={onFile}
      />
    </Box>
  );
}
